import { gql } from '@apollo/client';
import { FormControl, FormLabel, GridItem, useToast } from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIntervalWhen } from 'rooks';
import { useAuth } from '~auth/useAuth';
import { ConfigurationStatusType } from '~components/configurations/ConfigStateToggle';
import CopyButton from '~components/ui/CopyButton';
import { FormLabelPendingIndicator } from '~components/ui/FormLabelPendingIndicator';
import { DisplayTimeZoneSelect, TimeZone } from '~components/ui/TimeZoneSelect';
import { useAnalyticsReporter } from '~utils/analytics';
import { fromError } from '~utils/errors';
import { checkStateToDisableTab } from '../ConfigDetailSettings';
// import { fromError } from '~utils/errors';
import {
  ConfigTimeSection_ConfigFragment,
  useConfigUpdateTimeZoneMutation,
} from './__generated__/ConfigTimeSection.graphql';

interface Props {
  config: ConfigTimeSection_ConfigFragment;
  configStatus?: ConfigurationStatusType;
}

export function ConfigTimeSection({ config, configStatus }: Props) {
  const timeZone: TimeZone | undefined = config.timeZone?.reported;
  const timeZoneValues = config.timeZone?.supportedValues;
  const toast = useToast();
  const [updateTimeZone, { loading }] = useConfigUpdateTimeZoneMutation();
  const [date, setDate] = useState<string>('');
  const analytics = useAnalyticsReporter();
  const { verifyUserPermissions } = useAuth();
  const { t } = useTranslation();

  const isTimezoneUpdateDisabled =
    checkStateToDisableTab(configStatus) ||
    !verifyUserPermissions([Permission.ConfigurationUpdate]);

  const handleChange = useCallback(
    async (timeZone: string) => {
      try {
        await updateTimeZone({
          variables: {
            input: {
              configurationId: config.id,
              timeZone,
            },
          },
          refetchQueries: ['CustomerConfigDetailPage'],
          awaitRefetchQueries: true,
        });

        analytics.track('displaySettingUpdate', { group: 'management', changeItem: 'timeZone' });
      } catch (err) {
        toast({
          status: 'error',
          title: t('displayTimeZoneupdateErr'),
          description: fromError(err, 'UpdateDisplayTimeZone'),
        });
      }
    },
    [analytics, config.id, toast, updateTimeZone, t],
  );

  const tick = useCallback(() => {
    if (!timeZone || timeZone === 'DISABLED') {
      setDate('');
    }

    const now = DateTime.now().setZone(timeZone);
    setDate(now.toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS));
  }, [timeZone]);
  useEffect(tick, [tick, config]);
  useIntervalWhen(tick, 1000, true, true);

  return (
    <GridItem
      colSpan={{
        base: 8,
        sm: 6,
        lg: 8,
        xl: 5,
      }}
    >
      {!timeZone ? (
        t('configTimeZoneInfoUnavailable')
      ) : (
        <FormControl mb="10">
          <FormLabel>
            {t('timeZone')}
            <FormLabelPendingIndicator isPending={loading} />
          </FormLabel>
          <DisplayTimeZoneSelect
            onChange={handleChange}
            value={timeZone}
            supportedValues={timeZoneValues}
            isDisabled={isTimezoneUpdateDisabled}
          />
        </FormControl>
      )}
      {!timeZone || timeZone === 'DISABLED' ? null : (
        <FormControl>
          <FormLabel>{t('localTime')}</FormLabel>
          <span
            style={{
              // Monospace
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '-.5px',
            }}
          >
            {date}
          </span>
          <CopyButton value={date} label={t('localTime')} ml="4" />
        </FormControl>
      )}
    </GridItem>
  );
}

ConfigTimeSection.graphql = {
  fragments: {
    ConfigTimeSection_config: gql`
      fragment ConfigTimeSection_config on ConfigurationModel {
        id
        firmware {
          android {
            version
          }
        }
        timeZone {
          reported
          supportedValues
        }
      }
    `,
  },
  mutations: {
    ConfigUpdateTimeZone: gql`
      mutation ConfigUpdateTimeZone($input: ConfigurationUpdateTimeZoneInput!) {
        configurationUpdateTimeZone(input: $input) {
          id
          timeZone {
            reported
          }
        }
      }
    `,
  },
};
